import {
  BeneficiaryKind,
  type FaceValueCriteria,
  faceValueCriteriaSchema,
  NO_FACE_VALUE_CRITERIA,
} from '@cpce/shared';
import Decimal from 'decimal.js';
import { useState } from 'react';
import { useUpdatePrestationPrice } from '@/api/prestations/prestations.mutations';
import DecimalInput from '@/components/DecimalInput';
import { FieldLabel } from '@/components/form/FieldLabel';
import { FormCheckboxGroup } from '@/components/form/FormCheckboxGroup';
import { FormModal } from '@/components/form/FormModal';
import { EMPLOYEE_STATUS_OPTIONS } from '@/features/beneficiaries/employees/employees.constants';
import { useZodForm, type ZodForm } from '@/hooks/useZodForm';

const BENEFICIARY_KIND_OPTIONS = [
  { value: BeneficiaryKind.employee, label: 'Salarié' },
  { value: BeneficiaryKind.ayantDroit, label: 'Ayant-droit' },
];

const labelsOf = <T,>(options: { value: T; label: string }[], values: T[]) =>
  options
    .filter((option) => values.includes(option.value))
    .map((option) => option.label)
    .join(' ou ');

function describeRange(min: number | null, max: number | null): string | null {
  const bound = (value: number) => value.toString().replace('.', ',');
  if (min !== null && max !== null) return `${bound(min)} à ${bound(max)}`;
  if (min !== null) return `à partir de ${bound(min)}`;
  return max !== null ? `jusqu'à ${bound(max)}` : null;
}

export function describeFaceValueCriteria(criteria: FaceValueCriteria): string {
  const statuses = labelsOf(EMPLOYEE_STATUS_OPTIONS, criteria.employeeStatuses);
  const ages = describeRange(criteria.ageMin, criteria.ageMax);
  const coefficients = describeRange(criteria.coefficientMin, criteria.coefficientMax);
  return [
    labelsOf(BENEFICIARY_KIND_OPTIONS, criteria.beneficiaryKinds),
    statuses && `Statut ${statuses}`,
    ages && `Âge ${ages}`,
    coefficients && `Coefficient ${coefficients}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function BoundField({
  form,
  fieldName,
  label,
}: {
  form: ZodForm<FaceValueCriteria>;
  fieldName: 'ageMin' | 'ageMax' | 'coefficientMin' | 'coefficientMax';
  label: string;
}) {
  const value = form.watch(fieldName);
  return (
    <FieldLabel label={label} error={form.formData.errors[fieldName]?.message}>
      <DecimalInput
        aria-label={label}
        value={value === null ? null : new Decimal(value)}
        onChange={(bound) => form.setValue(fieldName, bound === null ? null : bound.toNumber())}
        onlyPositive
      />
    </FieldLabel>
  );
}

interface FaceValueCriteriaModalProps {
  prestationId: number;
  price: { id: number; name: string; attributionCriteria: FaceValueCriteria | null };
  closeModal: () => void;
}

export function FaceValueCriteriaModal({
  prestationId,
  price,
  closeModal,
}: FaceValueCriteriaModalProps) {
  const update = useUpdatePrestationPrice(prestationId, price.id);
  const [defaultValues] = useState(price.attributionCriteria ?? NO_FACE_VALUE_CRITERIA);
  const form = useZodForm<FaceValueCriteria>({
    schema: faceValueCriteriaSchema,
    defaultValues,
    onSubmit: async (attributionCriteria) => {
      await update.mutate({ attributionCriteria });
      closeModal();
    },
  });

  return (
    <FormModal
      title={`Critères d'attribution : ${price.name}`}
      open
      onOpenChange={(open) => !open && closeModal()}
      onSubmit={form.submit}
      resetForm={form.resetForm}
      isPending={update.isPending}
      submitLabel="Enregistrer"
    >
      <p className="text-sm text-gray-600">
        Un champ laissé vide ne filtre pas. Sans aucun critère, cette valeur ne s'attribue qu'à la
        main. L'âge s'entend à la date de l'inscription ; un ayant-droit porte le statut et le
        coefficient de son salarié.
      </p>
      <FormCheckboxGroup
        label="Bénéficiaire"
        fieldName="beneficiaryKinds"
        formProps={form.formData}
        options={BENEFICIARY_KIND_OPTIONS}
      />
      <FormCheckboxGroup
        label="Statut"
        fieldName="employeeStatuses"
        formProps={form.formData}
        options={EMPLOYEE_STATUS_OPTIONS}
      />
      <div className="grid grid-cols-2 gap-4">
        <BoundField form={form} fieldName="ageMin" label="Âge minimum" />
        <BoundField form={form} fieldName="ageMax" label="Âge maximum" />
        <BoundField form={form} fieldName="coefficientMin" label="Coefficient minimum" />
        <BoundField form={form} fieldName="coefficientMax" label="Coefficient maximum" />
      </div>
    </FormModal>
  );
}
